import { useCallback, useMemo, useState } from 'react'
import { number, object, SchemaOf, string } from 'yup'
import { Map } from 'immutable'
import { useSelector } from 'react-redux'
import { selectBrooklynServerUrl } from '../../config/redux/selectors'
import { selectAuth } from '../../auth/redux/selectors'
import { upsertMedia as upsertMediaQuery } from '../../media'
import { Result } from '../Result'
import { useLazyFetchYIContentPolicyId } from '../policy-id/useLazyFetchYIContentPolicyId'

type MediaParams = {
  contentId: string
  policyId: number
  mediaMaestroTitleCode?: string | null
  use?: string | null
  subtype?: string | null
}

type FormValues = Omit<MediaParams, 'policyId'>

const upsertMediaValidationSchema: SchemaOf<MediaParams> = object({
  contentId: string().required(),
  policyId: number().required(),
  mediaMaestroTitleCode: string(),
  use: string(),
  subtype: string(),
})

type MediaCreationResopnse = {
  mediaId: string
}
// Response doesn't have types, so we type guard our expectations
const responseValidationSchema: SchemaOf<MediaCreationResopnse> = object({
  mediaId: string().required(),
})

// Form initial params are set to '' for selectors,
// but it's not valid param for request, so we need to convert them to null
function convertToParams (values: FormValues, policyId: number): MediaParams {
  const valuesEntries = Object.entries(values)
  const paramsEntries = valuesEntries.map(([key, value]) =>
    value === '' ? [key, null] : [key, value],
  )
  const params = Object.fromEntries(paramsEntries)
  const paramsWithPolicyId = {
    ...params,
    // mediaMaestroTitleCode is used in Gaia publishing/digest system
    // YI doesn't support it now, content-migration uses '-1' as a fallback
    // for consistency for new content here we use the same '-1'
    mediaMaestroTitleCode: -1,
    policyId,
  }
  return paramsWithPolicyId
}

export function useUpsertMedia ({
  onModificationApplied,
}: { onModificationApplied?: () => void } = {}) {
  const authDetails = useSelector<unknown, Map<string, unknown>>(selectAuth)
  const { isLoading: isConfigLoading, url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const [error, setError] = useState<Error | string | null>(null)

  const [fetchYIContentPolicyId] = useLazyFetchYIContentPolicyId()
  const create = useCallback(
    async (values: FormValues) => {
      let mediaUpsertResult: Map<string, unknown> | null = null
      const yiContentPolicyId = await fetchYIContentPolicyId()
      if (!yiContentPolicyId) throw new Error('YI Content Policy ID not found')

      const params = convertToParams(values, yiContentPolicyId)

      const paramsOrError = Result.encase(() => upsertMediaValidationSchema.validateSync(params))()

      if (paramsOrError.status === 'fail') {
        return paramsOrError
      }

      try {
        mediaUpsertResult = await upsertMediaQuery({
          auth: authDetails,
          server: brooklynUrl,
          data: Map(paramsOrError.result),
        })
      } catch (e) {
        const errorMsg = typeof e === 'string' || e instanceof Error ? e : 'unknonw type of error'
        setError(errorMsg)
        return Result.fail(errorMsg)
      }
      if (mediaUpsertResult === null) {
        const errorMsg = 'media upsert failed'
        setError(errorMsg)
        return Result.fail(errorMsg)
      }
      const responseOrError = Result.encase(() =>
        responseValidationSchema.validateSync({
          mediaId: mediaUpsertResult.get('mediaId'),
        }),
      )()
      if (responseOrError.status === 'fail') {
        setError(responseOrError.error)
        return responseOrError
      }
      if (typeof onModificationApplied === 'function') {
        onModificationApplied()
      }
      return Result.ok(responseOrError.result)
    },
    [authDetails, brooklynUrl, onModificationApplied, fetchYIContentPolicyId],
  )

  const tuple = useMemo(
    () => [create, { loading: isConfigLoading, error }] as const,
    [create, isConfigLoading, error],
  )
  return tuple
}
